import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useGetSimulationFxLocksQuery, useRecreateSimulationMutation } from '@/api/v1';
import { CustomFees, FxLockType, SimulationResponse } from '@/types/simulation';

interface UseFxLockOptionsParameters {
  readonly parentSimulationId: string | null;
  readonly enabled: boolean;
  readonly markupBps: number | null;
}

interface UseFxLockOptionsReturn {
  readonly fxLockOptions: readonly SimulationResponse[];
  readonly selectedFxLockType: FxLockType;
  readonly selectFxLockType: (type: FxLockType) => void;
  readonly isLoading: boolean;
  readonly effectiveSimulationId: string | null;
  readonly effectiveSimulation: SimulationResponse | null;
  readonly setRecreatedSimulation: (simulation: SimulationResponse | null) => void;
}

export const useFxLockOptions = (
  parameters: UseFxLockOptionsParameters,
): UseFxLockOptionsReturn => {
  const { parentSimulationId, enabled, markupBps } = parameters;

  const [selectedFxLockType, setSelectedFxLockType] = React.useState<FxLockType>(
    FxLockType.DEFAULT,
  );

  const { data: fxLockOptions = [], isFetching: isFetchingLocks } = useGetSimulationFxLocksQuery(
    parentSimulationId!,
    {
      skip: !enabled || !parentSimulationId,
    },
  );

  const [recreateSimulation, recreateMutation] = useRecreateSimulationMutation();
  const [recreatedSimulation, setRecreatedSimulation] = React.useState<SimulationResponse | null>(
    null,
  );

  const selectedLockSimulation = React.useMemo((): SimulationResponse | null => {
    if (fxLockOptions.length === 0) {
      return null;
    }

    return (
      fxLockOptions.find(
        (option: SimulationResponse): boolean =>
          (option.fxLockType ?? FxLockType.DEFAULT) === selectedFxLockType,
      ) ?? null
    );
  }, [fxLockOptions, selectedFxLockType]);

  const customFees = React.useMemo((): CustomFees | null => {
    if (markupBps === null || markupBps === 0) {
      return null;
    }

    return { INTEGRATOR_SPREAD: markupBps };
  }, [markupBps]);

  // The simulation ID to recreate with markup: lock child if available, otherwise parent
  const simulationIdToRecreate = selectedLockSimulation?.id ?? parentSimulationId;

  // Clear recreated simulation when the recreate target changes
  React.useEffect((): void => {
    setRecreatedSimulation(null);
  }, [simulationIdToRecreate]);

  // Recreate simulation with markup when inputs change
  React.useEffect((): void => {
    if (!simulationIdToRecreate || !customFees) {
      setRecreatedSimulation(null);
      return;
    }

    // Skip if we already have a recreated result (e.g. from FxMarkupModal on Apply)
    if (recreatedSimulation) {
      return;
    }

    recreateSimulation({
      simulationId: simulationIdToRecreate,
      customFees: customFees,
    });
  }, [simulationIdToRecreate, customFees, recreatedSimulation, recreateSimulation]);

  // Handle recreate mutation result
  React.useEffect((): void => {
    if (recreateMutation.status === QueryStatus.fulfilled && recreateMutation.data) {
      setRecreatedSimulation(recreateMutation.data);
      recreateMutation.reset();
    } else if (recreateMutation.status === QueryStatus.rejected) {
      setRecreatedSimulation(null);
      recreateMutation.reset();
    }
  }, [recreateMutation]);

  // The effective simulation: recreated (if markup) > selected lock child > null
  const effectiveSimulation = React.useMemo((): SimulationResponse | null => {
    if (customFees && recreatedSimulation) {
      return recreatedSimulation;
    }
    return selectedLockSimulation;
  }, [customFees, recreatedSimulation, selectedLockSimulation]);

  const effectiveSimulationId = React.useMemo((): string | null => {
    return effectiveSimulation?.id ?? parentSimulationId;
  }, [effectiveSimulation, parentSimulationId]);

  const isLoading = isFetchingLocks || recreateMutation.status === QueryStatus.pending;

  return {
    fxLockOptions,
    selectedFxLockType,
    selectFxLockType: setSelectedFxLockType,
    isLoading,
    effectiveSimulationId,
    effectiveSimulation,
    setRecreatedSimulation,
  };
};
